import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronRight, Settings, Share2 } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * GA4ChannelsChart - Composant pour afficher les canaux d'acquisition Google Analytics 4
 * Design identique au composant MatomoChannelsChart
 */
const GA4ChannelsChart = ({
  channels = [],
  previousChannels = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  summaryBounceRate = null,
  conversionLabel = 'Conversions',
  serverTotals = null,
  serverPreviousTotals = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_channels',
  // Props pour les options avancées (conversions)
  token = null,
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [expandedChannels, setExpandedChannels] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant Canaux (réactif aux changements)
  const componentMetrics = useComponentMetrics('ga4_channels', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Ordre des métriques à afficher (depuis la config)
  const metricsOrder = componentMetrics.order || componentMetrics.metrics || [];

  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Mapping des noms de canaux GA4 vers des noms français
  const channelNameMapping = {
    'Direct': 'Accès direct',
    'Organic Search': 'Recherche organique',
    'Paid Search': 'Recherche payante',
    'Organic Social': 'Social organique',
    'Paid Social': 'Social payant',
    'Email': 'E-mail',
    'Referral': 'Sites référents',
    'Display': 'Display',
    'Affiliates': 'Affiliés',
    'Video': 'Vidéo',
    'Paid Video': 'Vidéo payante',
    'Organic Video': 'Vidéo organique',
    'Cross-network': 'Cross-network',
    'Unassigned': 'Non attribué',
    '(not set)': 'Non défini'
  };

  // Normaliser les données des canaux
  const channelData = useMemo(() => {
    if (!channels || !Array.isArray(channels) || channels.length === 0) {
      return [];
    }

    return channels.map(channel => {
      const rawName = channel.channel || channel.name || 'Autre';
      const name = channelNameMapping[rawName] || rawName;

      const sessions = channel.sessions || 0;
      const conversions = channel.conversions || 0;
      const conversionsValue = channel.conversionsValue || channel.revenue || 0;
      const transactions = channel.transactions || 0;
      const totalRevenue = channel.totalRevenue || conversionsValue || 0;

      // Normaliser les détails (sources dans ce canal)
      const details = (channel.sources || []).map(source => {
        const srcSessions = source.sessions || 0;
        const srcConversions = source.conversions || 0;
        const srcTransactions = source.transactions || 0;
        const srcTotalRevenue = source.totalRevenue || source.conversionsValue || 0;
        return {
          label: source.source || 'Inconnu',
          sessions: srcSessions,
          activeUsers: source.activeUsers || 0,
          pageviews: source.pageviews || 0,
          conversions: srcConversions,
          conversionRate: srcSessions > 0 ? (srcConversions / srcSessions * 100) : 0,
          conversionsValue: source.conversionsValue || 0,
          transactions: srcTransactions,
          totalRevenue: srcTotalRevenue,
          transactionRate: srcSessions > 0 ? (srcTransactions / srcSessions * 100) : 0,
          bounceRate: source.bounceRate || 0
        };
      });

      return {
        name,
        rawName,
        sessions,
        activeUsers: channel.activeUsers || 0,
        pageviews: channel.pageviews || 0,
        conversions,
        conversionRate: sessions > 0 ? (conversions / sessions * 100) : 0,
        conversionsValue,
        transactions,
        totalRevenue,
        transactionRate: sessions > 0 ? (transactions / sessions * 100) : 0,
        bounceRate: channel.bounceRate || 0,
        avgSessionDuration: channel.avgSessionDuration || 0,
        details,
        hasDetails: details.length > 0
      };
    }).filter(c => c.sessions > 0);
  }, [channels]);

  // Calcul des totaux
  const totals = useMemo(() => {
    // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
    // calculé pour l'ensemble du site, jamais une addition des lignes reçues.
    if (serverTotals && serverTotals.sessions > 0) return serverTotals;

    if (!channelData.length) return {};

    const baseTotals = channelData.reduce((acc, channel) => ({
      sessions: acc.sessions + (channel.sessions || 0),
      activeUsers: acc.activeUsers + (channel.activeUsers || 0),
      pageviews: acc.pageviews + (channel.pageviews || 0),
      bounceSum: acc.bounceSum + ((channel.bounceRate || 0) * (channel.sessions || 0)),
      durationSum: acc.durationSum + ((channel.avgSessionDuration || 0) * (channel.sessions || 0)),
      conversions: acc.conversions + (channel.conversions || 0),
      conversionsValue: acc.conversionsValue + (channel.conversionsValue || 0),
      transactions: acc.transactions + (channel.transactions || 0),
      totalRevenue: acc.totalRevenue + (channel.totalRevenue || 0)
    }), { sessions: 0, activeUsers: 0, pageviews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    return {
      ...baseTotals,
      bounceRate: baseTotals.sessions > 0 ? (baseTotals.bounceSum / baseTotals.sessions) : 0,
      avgSessionDuration: baseTotals.sessions > 0 ? (baseTotals.durationSum / baseTotals.sessions) : 0,
      conversionRate: baseTotals.sessions > 0 ? (baseTotals.conversions / baseTotals.sessions * 100) : 0,
      transactionRate: baseTotals.sessions > 0 ? (baseTotals.transactions / baseTotals.sessions * 100) : 0
    };
  }, [channelData, serverTotals]);

  // Calcul des totaux de la période précédente pour comparaison
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousChannels || !previousChannels.length) return null;

    // Même règle que pour le total courant : la période de comparaison a elle aussi
    // son total calculé par GA4, sinon la variation compare deux choses différentes.
    if (serverPreviousTotals && serverPreviousTotals.sessions > 0) return serverPreviousTotals;

    const baseTotals = previousChannels.reduce((acc, channel) => ({
      sessions: acc.sessions + (channel.sessions || 0),
      activeUsers: acc.activeUsers + (channel.activeUsers || 0),
      pageviews: acc.pageviews + (channel.pageviews || 0),
      bounceSum: acc.bounceSum + ((channel.bounceRate || 0) * (channel.sessions || 0)),
      durationSum: acc.durationSum + ((channel.avgSessionDuration || 0) * (channel.sessions || 0)),
      conversions: acc.conversions + (channel.conversions || 0),
      conversionsValue: acc.conversionsValue + (channel.conversionsValue || channel.revenue || 0),
      transactions: acc.transactions + (channel.transactions || 0),
      totalRevenue: acc.totalRevenue + (channel.totalRevenue || 0)
    }), { sessions: 0, activeUsers: 0, pageviews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    return {
      ...baseTotals,
      bounceRate: baseTotals.sessions > 0 ? (baseTotals.bounceSum / baseTotals.sessions) : 0,
      avgSessionDuration: baseTotals.sessions > 0 ? (baseTotals.durationSum / baseTotals.sessions) : 0,
      conversionRate: baseTotals.sessions > 0 ? (baseTotals.conversions / baseTotals.sessions * 100) : 0,
      transactionRate: baseTotals.sessions > 0 ? (baseTotals.transactions / baseTotals.sessions * 100) : 0
    };
  }, [compareEnabled, previousChannels, serverPreviousTotals]);

  // Données triées
  const sortedChannels = useMemo(() => {
    if (!channelData.length) return [];

    const sorted = [...channelData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [channelData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedChannels.length / ITEMS_PER_PAGE);

  // Revenir à la première page si les données raccourcissent la liste
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedChannels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedChannels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedChannels, currentPage]);

  // Données pour le pie chart Sessions
  const sessionsChartData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel.sessions || 0,
        percentage: totals.sessions > 0 ? ((channel.sessions / totals.sessions) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.sessions]);

  // Données pour le pie chart Conversions
  const conversionsChartData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel.conversions || 0,
        percentage: totals.conversions > 0 ? ((channel.conversions / totals.conversions) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.conversions]);

  // Données pour le pie chart Transactions (e-commerce)
  const transactionsChartData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel.transactions || 0,
        percentage: totals.transactions > 0 ? ((channel.transactions / totals.transactions) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.transactions]);

  // Détermine si on affiche Transactions ou Conversions dans le pie chart
  const showTransactionsPieChart = isMetricActive('transactions') && !isMetricActive('conv');
  const pieChartData = showTransactionsPieChart ? transactionsChartData : conversionsChartData;
  const pieChartLabel = showTransactionsPieChart
    ? 'Transactions'
    : libelleConversions(conversionConfig, conversionLabel);
  const pieChartTotal = showTransactionsPieChart ? totals.transactions : totals.conversions;

  // Mapping canal → couleur (basé sur l'ordre original de channelData)
  const channelColorMap = useMemo(() => {
    const map = {};
    channelData.forEach((channel, index) => {
      map[channel.name] = COLORS[index % COLORS.length];
    });
    return map;
  }, [channelData]);

  // Données pour la légende
  const legendData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData.map((channel, index) => ({
      value: channel.name,
      color: COLORS[index % COLORS.length]
    }));
  }, [channelData]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  // Configuration des colonnes du tableau
  const columnConfig = useMemo(() => ({
    visites: {
      key: 'sessions',
      label: 'Sessions',
      getValue: (channel) => channel.sessions,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.sessions || 0
    },
    visiteurs: {
      key: 'activeUsers',
      label: 'Utilisateurs',
      getValue: (channel) => channel.activeUsers,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.activeUsers || 0
    },
    rebond: {
      key: 'bounceRate',
      label: '% de Rebond',
      getValue: (channel) => channel.bounceRate,
      format: (val) => `${formatNumber(val, 1)}%`,
      getPrevValue: (prev) => prev?.bounceRate || 0,
      inverted: true
    },
    pages_vues: {
      key: 'pageviews',
      label: 'Pages vues',
      getValue: (channel) => channel.pageviews,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.pageviews || 0
    },
    taux_conv: {
      key: 'conversionRate',
      label: libelleTauxConversion(conversionConfig, conversionLabel),
      getValue: (channel) => channel.conversionRate,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.conversions / prev.sessions * 100) : 0
    },
    conv: {
      key: 'conversions',
      label: libelleConversions(conversionConfig, conversionLabel),
      getValue: (channel) => channel.conversions,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.conversions || 0
    },
    valeur_conv: {
      key: 'conversionsValue',
      label: 'Valeur Conv.',
      getValue: (channel) => channel.conversionsValue,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.conversionsValue || prev?.revenue || 0
    },
    transactions: {
      key: 'transactions',
      label: 'Transactions',
      getValue: (channel) => channel.transactions,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.transactions || 0
    },
    chiffre_affaires: {
      key: 'totalRevenue',
      label: 'Chiffre d\'affaires',
      getValue: (channel) => channel.totalRevenue,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.totalRevenue || 0
    },
    taux_transaction: {
      key: 'transactionRate',
      label: '% Conversion (transactions)',
      getValue: (channel) => channel.transactionRate,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.transactions / prev.sessions * 100) : 0
    },
    panier_moyen: {
      key: 'avgOrderValue',
      label: 'Panier moyen',
      getValue: (channel) => channel.transactions > 0 ? channel.totalRevenue / channel.transactions : 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev && prev.transactions > 0 ? (prev.totalRevenue || 0) / prev.transactions : 0
    },
    duree_moy: {
      key: 'avgSessionDuration',
      label: 'Durée moyenne',
      getValue: (channel) => channel.avgSessionDuration || 0,
      format: (val) => {
        const mins = Math.floor(val / 60);
        const secs = Math.floor(val % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      },
      getPrevValue: (prev) => prev?.avgSessionDuration || 0
    },
    pages_visite: {
      key: 'pagesPerSession',
      label: 'Pages/Session',
      getValue: (channel) => channel.sessions > 0 ? (channel.pageviews || 0) / channel.sessions : 0,
      format: (val) => formatNumber(val, 1),
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.pageviews || 0) / prev.sessions : 0
    }
  }), [formatNumber, conversionLabel, conversionConfig]);

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handler pour déplier/replier un canal
  const toggleChannel = (channelName) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelName]: !prev[channelName]
    }));
  };

  // Ne pas afficher si pas de données ou en chargement
  if (loading) {
    return (
      <div className="device-conversions-section">
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Share2 size={18} />
            Canaux
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </div>
    );
  }

  if (!channelData.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Share2 size={18} />
          Canaux
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Canaux GA4"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Sessions (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par canal</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={sessionsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {sessionsChartData.map((entry, index) => (
                            <Cell key={`sessions-cell-${index}`} fill={channelColorMap[entry.name] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Légende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendeVisible.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                    {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                    {legendeReste > 0 && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        + {legendeReste} autres
                      </div>
                    )}
                  </div>

                  {/* Pie chart Conversions/Transactions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{pieChartLabel} par canal</h5>
                    {pieChartTotal > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`pie-cell-${index}`} fill={channelColorMap[entry.name] || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [formatNumber(value) + ` ${pieChartLabel.toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        Aucune {pieChartLabel.toLowerCase()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Canal {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {metricsOrder.map(metricKey => {
                      if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                      const col = columnConfig[metricKey];
                      return (
                        <th key={metricKey} onClick={() => handleSort(col.key)} className="sortable numeric">
                          <span className="th-libelle">{col.label}</span> {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {paginatedChannels.map((channel, index) => {
                    const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
                    const prev = compareEnabled && previousChannels ? previousChannels.find(p => p.channel === channel.rawName || p.name === channel.rawName) : null;

                    return (
                    <React.Fragment key={index}>
                      {/* Ligne principale du canal */}
                      <tr
                        className={channel.hasDetails ? 'expandable-row' : ''}
                        onClick={() => channel.hasDetails && toggleChannel(channel.name)}
                        style={{ cursor: channel.hasDetails ? 'pointer' : 'default' }}
                      >
                        <td className="device-name">
                          {channel.hasDetails && (
                            <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                              {expandedChannels[channel.name] ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </span>
                          )}
                          <span
                            className="device-indicator"
                            style={{ backgroundColor: channelColorMap[channel.name] || COLORS[globalIndex % COLORS.length] }}
                          ></span>
                          {channel.name}
                        </td>
                        {metricsOrder.map(metricKey => {
                          if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                          const col = columnConfig[metricKey];
                          const value = col.getValue(channel);
                          const prevValue = prev ? col.getPrevValue(prev) : null;
                          const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_channels', accountId);
                          return (
                            <td key={metricKey} className={`numeric ${objectiveClass}`}>
                              {col.format(value)}
                              {prev && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Sous-lignes de détails (sources) */}
                      {expandedChannels[channel.name] && channel.details.map((detail, detailIndex) => {
                        const prevChannel = compareEnabled && previousChannels ? previousChannels.find(p => p.channel === channel.rawName || p.name === channel.rawName) : null;
                        const prevSource = prevChannel?.sources?.find(s => s.source === detail.label);

                        return (
                          <tr
                            key={`${index}-detail-${detailIndex}`}
                            className="detail-row"
                            style={{
                              backgroundColor: '#f9fafb',
                              fontSize: '0.9em'
                            }}
                          >
                            <td className="device-name" style={{ paddingLeft: '36px' }}>
                              <span style={{ color: '#6b7280' }}>└</span> {detail.label}
                            </td>
                            {metricsOrder.map(metricKey => {
                              if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                              const col = columnConfig[metricKey];
                              const value = col.getValue(detail);
                              const prevValue = prevSource ? col.getPrevValue(prevSource) : null;
                              const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_channels', accountId);
                              return (
                                <td key={metricKey} className={`numeric ${objectiveClass}`} style={{ color: objectiveClass ? undefined : '#6b7280' }}>
                                  {col.format(value)}
                                  {prevSource && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );})}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {metricsOrder.map(metricKey => {
                      if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                      const col = columnConfig[metricKey];
                      // Cas spécial pour le rebond : utiliser summaryBounceRate si disponible
                      const value = metricKey === 'rebond' ? (summaryBounceRate ?? totals.bounceRate ?? 0) : col.getValue(totals);
                      const prevValue = previousTotals ? col.getPrevValue(previousTotals) : null;
                      const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_channels', accountId);
                      return (
                        <td key={metricKey} className={`numeric ${objectiveClass}`}>
                          <strong>{col.format(value)}</strong>
                          {previousTotals && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedChannels.length)} sur {sortedChannels.length} canaux
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_channels"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4ChannelsChart;
